import axios from "axios";

const RAW_BASE = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/+$/, "");

// Si el .env ya trae /api, evitamos duplicarlo; si no lo trae, lo agregamos.
const API_ROOT = RAW_BASE.match(/\/api$/i) ? RAW_BASE : `${RAW_BASE}/api`;
const API_URL  = `${API_ROOT}/articulos`;

/* ========== helpers ========== */
const pickFirst = (...vals) => {
  for (const v of vals) if (v !== undefined && v !== null) return v;
  return undefined;
};

function enrichWithEmpresa(obj, empresaId) {
  const e = Number.isFinite(+empresaId) ? +empresaId : undefined;
  return {
    ...obj,
    empresaId: e,
    EmpresaId: e,
    empresa: e,
    Empresa: e,
  };
}

// Limpia y adapta el payload al backend
function sanitizeForApi(prod, empresaId) {
  const out = { ...prod };

  // ID -> int o fuera (creación)
  const idNum = Number(out.ID ?? out.id);
  if (!Number.isFinite(idNum) || idNum <= 0) {
    delete out.ID;
    delete out.id;
  } else {
    out.ID = idNum;
    delete out.id;
  }

  // Nombre / descripción / producto (por compatibilidad con APIs)
  const nombreVal = pickFirst(out.descripcion, out.Descripcion, out.nombre, out.Nombre, out.producto, out.Producto, "");
  if (nombreVal) {
    if (!out.descripcion) out.descripcion = nombreVal;
    if (!out.Descripcion) out.Descripcion = nombreVal;
    if (!out.nombre) out.nombre = nombreVal;
    if (!out.Nombre) out.Nombre = nombreVal;
    if (!out.producto) out.producto = nombreVal;
    if (!out.Producto) out.Producto = nombreVal;
  }

  // Unidad / claves SAT
  out.unidadMedida = pickFirst(out.unidadMedida, out.UnidadMedida, out.unidad, out.Unidad, out.claveUnidad, out.ClaveUnidad, "");
  out.claveUnidad  = pickFirst(out.claveUnidad, out.ClaveUnidad, out.unidadMedida, out.UnidadMedida, out.unidad, out.Unidad, "");
  out.claveSAT     = pickFirst(out.claveSAT, out.ClaveSAT, out.claveSat, out.ClaveSat, "");

  // Precio
  const precio = Number(pickFirst(out.precioUnitario, out.precio_Unitario, out.PrecioUnitario, out.precio, out.Precio));
  if (Number.isFinite(precio)) {
    out.precioUnitario = precio;
    out.precio_Unitario = precio; // por si el backend usa este casing
  } else {
    delete out.precioUnitario;
    delete out.precio_Unitario;
  }

  // Estatus a "A"/"I"
  if (out.estatus === true) out.estatus = "A";
  if (out.estatus === false) out.estatus = "I";

  return enrichWithEmpresa(out, empresaId);
}

// Normaliza lo que viene del backend a tu UI
export function normalizarArticulo(p) {
  if (!p) return null;
  return {
    ID: pickFirst(p.ID, p.id, p.Id, null),
    id: pickFirst(p.ID, p.id, p.Id, null),
    clave: pickFirst(p.clave, p.Clave, ""),
    descripcion: pickFirst(p.descripcion, p.Descripcion, p.nombre, p.Nombre, p.producto, p.Producto, ""),
    unidadMedida: pickFirst(p.unidadMedida, p.UnidadMedida, p.unidad, p.Unidad, p.claveUnidad, p.ClaveUnidad, ""),
    claveUnidad: pickFirst(p.claveUnidad, p.ClaveUnidad, p.unidadMedida, p.UnidadMedida, ""),
    precioUnitario: Number(p.precioUnitario ?? p.precio_Unitario ?? p.PrecioUnitario ?? p.precio ?? p.Precio) || 0,
    claveSAT: pickFirst(p.claveSAT, p.ClaveSAT, p.claveSat, p.ClaveSat, ""),
    estatus: pickFirst(p.estatus, p.Estatus, "A"),
    empresa: pickFirst(p.empresa, p.Empresa, p.empresaId, p.EmpresaId, null),
    stock: p.stock ?? p.Stock,
    existencia: p.existencia ?? p.Existencia,
  };
}

const extractOne = (resp) => {
  const d = resp?.data;
  if (!d) return null;
  if (Array.isArray(d?.data)) return d.data[0] ?? null;
  if (Array.isArray(d?.Data)) return d.Data[0] ?? null;
  return d.data ?? d;
};

/* ========== API ========== */

export const obtenerArticulos = (empresaId, pageNumber=1, pageSize=10, search="", estatus="") =>
  axios.get(API_URL, {
    params: {
      empresaId,
      pageNumber,
      pageSize,
      search: (search || "").trim(),
      estatus: (estatus || "").trim(),
    },
  });

export const crearArticulo = (articulo, empresaId) =>
  axios.post(API_URL, sanitizeForApi(articulo, empresaId));

export const actualizarArticulo = (id, articulo, empresaId) =>
  axios.put(`${API_URL}/${id}`, sanitizeForApi(articulo, empresaId));

export const eliminarArticulo = (id) =>
  axios.delete(`${API_URL}/${id}`);

export const obtenerArticuloPorId = (id) =>
  axios.get(`${API_URL}/${id}`);

export async function buscarArticuloPorClave(empresaId, clave) {
  try {
    const { data } = await axios.get(`${API_URL}/by-clave`, {
      params: { empresaId, clave },
    });
    return data;
  } catch (e) {
    if (e?.response?.status === 404) return null;
    throw e;
  }
}

export async function obtenerArticuloDetalle({ id, empresaId, clave }) {
  if (id) {
    try {
      const r = await obtenerArticuloPorId(id);
      const uno = extractOne(r);
      const norm = normalizarArticulo(uno);
      if (norm) return norm;
    } catch {}
  }

  if (empresaId && clave) {
    try {
      const r = await buscarArticuloPorClave(empresaId, clave);
      const norm = normalizarArticulo(r?.data ?? r);
      if (norm) return norm;
    } catch {}
  }

  if (empresaId && clave) {
    try {
      const r = await obtenerArticulos(empresaId, 1, 1, clave, "");
      const uno =
        (Array.isArray(r?.data?.Data) && r.data.Data[0]) ||
        (Array.isArray(r?.data?.data) && r.data.data[0]) ||
        null;
      const norm = normalizarArticulo(uno);
      if (norm) return norm;
    } catch {}
  }

  return null;
}
